import { config, isLiveApi } from "./config";
import { getLocale, t } from "./i18n";

/**
 * Client of the HRNS.Platform.Server **public (anonymous)** staffing wizard API.
 *
 * Route convention: `api/[controller]/[action]` (BaseController) — the anonymous actions
 * implemented in HRNS.Platform.Server (branch `staffing-agency-module`; PRD §5.1):
 *   - POST api/StaffingAgency/GetPublicStaffingAgenciesAsync        (agencies by PublicProviderSite host)
 *   - POST api/StaffingCandidate/RegisterStaffingCandidateAsync    (candidate created Pending)
 *   - POST api/StaffingCandidate/RequestContactVerificationAsync   (e-mail/WhatsApp code)
 *   - POST api/StaffingCandidate/ConfirmContactVerificationAsync   (code entered in web app)
 *   - POST api/StaffingCandidate/ResendCandidatePasswordLinkAsync  (password-setup link again)
 *   - POST api/StaffingCandidate/GetStaffingScreeningQuestionsAsync ((agency, country)-scoped)
 *   - POST api/StaffingCandidate/GetStaffingAgencyDocumentTypesAsync (per-agency sign-up documents)
 *   - POST api/StaffingCandidate/SaveRegistrationDocumentFileAsync  (multipart doc upload)
 *   - POST api/WorkCategory/GetWorkCategoriesAsync · api/JobType/GetJobTypesAsync · api/Region/GetRegionsAsync
 *
 * Query/Load wire shape (LoadDTOWebRequest<TDTO, TPropsFilter>): `{ PropsFilter: { … } }`
 * — the request host (ProviderHost) is set server-side from the HTTP request.
 *
 * Everything the wizard shows is **scoped by the agency selected on the landing page**
 * (PRD §5.1, §13 row 43, 2026-10-02): job types = the agency's proposed jobs, regions by the
 * agency's activity country, screening questions/document types per (selected agency, country).
 */
/**
 * Platform route convention (discovered on the RUNNING server, swagger /api/docs/v1:
 * `api/[controller]/[action]` with the **"Async" suffix STRIPPED from the action URLs**
 * (options.SuppressAsyncSuffixInActionNames): e.g. api/StaffingAgency/GetPublicStaffingAgencies.
 * C# methods keep the Async name in code; the URL never has it.
 */
export const ENDPOINTS = {
  register: "api/StaffingCandidate/RegisterStaffingCandidate",
  requestVerification: "api/StaffingCandidate/RequestContactVerification",
  confirmVerification: "api/StaffingCandidate/ConfirmContactVerification",
  /** T-SA-18.14: the password-setup link again (confirmed e-mail, no password yet). */
  resendPasswordLink: "api/StaffingCandidate/ResendCandidatePasswordLink",
  screeningQuestions: "api/StaffingCandidate/GetStaffingScreeningQuestions",
  agencyDocumentTypes: "api/StaffingCandidate/GetStaffingAgencyDocumentTypes",
  workCategories: "api/WorkCategory/GetWorkCategories",
  jobTypes: "api/JobType/GetJobTypes",
  regions: "api/Region/GetRegions",
  publicAgencies: "api/StaffingAgency/GetPublicStaffingAgencies",
  /** The SELECTED AGENCY'S registration dictionary (from its CONTRACT — the system
   * dictionaries are admin tables; 2026-10-02 ruling). */
  agencyWizardData: "api/StaffingAgency/GetStaffingAgencyWizardData",
  registrationDocument: "api/StaffingCandidate/SaveRegistrationDocumentFile",
  /** Telegram chatbot binding (chatbot PRD §5.2): token request + status poll. */
  requestTelegramBinding: "api/StaffingCandidate/RequestTelegramBinding",
  requestWhatsAppLink: "api/StaffingCandidate/RequestWhatsAppLink",
  getTelegramBindingStatus: "api/StaffingCandidate/GetTelegramBindingStatus",
  chatbotBindingStatus: "api/StaffingCandidate/GetChatbotBindingStatus",
  /** The TWO-STEP Telegram confirmation (2026-10-10), step 2's re-send: the [Confirm phone
   *  number by Telegram] button re-delivers the phone-share consent message in the chat. */
  resendTelegramPhoneRequest: "api/StaffingCandidate/ResendTelegramPhoneRequest",
  /** The deployment's PUBLIC chatbot config (2026-10-03 ruling): the site builds the
   * /start deep link from SERVER data — the VITE env var is only a fallback. */
  siteChatbotConfig: "api/StaffingAgency/GetSiteChatbotConfig",
} as const;

/**
 * The platform API returns **camelCase** JSON always (System.Text.Json web defaults,
 * user ruling 2026-10-02); requests deserialize case-insensitively (proven live — the
 * PascalCase save/STO/filter payloads of this client work unchanged). The RESPONSES are
 * normalized here: every JSON key is recursively upper-cased (camelCase → PascalCase,
 * a no-op for keys already PascalCase) so the typed wire models below read correctly
 * whatever casing the endpoint serialization emits.
 */
function normalizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key.charAt(0).toUpperCase() + key.slice(1)] = normalizeKeys(val);
    }
    return out;
  }
  return value;
}

/**
 * The API base — the ENDPOINTS below are written relative to the platform API root
 * (`api/[controller]/[action]`). The base accepts BOTH forms without double-prefixing
 * (the "/api/api/..." bug seen on the same-origin proxy):
 *   - a full server URL ("https://server")  → "https://server/api" is appended, and
 *   - a same-origin API prefix ("/api")     → used as-is (it already IS the api root).
 */
const apiBase = () => {
  const raw = (config.apiBaseUrl || "").replace(/\/+$/, "");
  return /\/api$/i.test(raw) ? raw : `${raw}/api`;
};
const BASE = apiBase();
const url = (ep: string) => {
  const path = ep.replace(/^api\//, "").replace(/^\//, "").replace(/Async$/i, "");
  return `${BASE}/${path}`;
};

async function post<TReq, TRes>(endpoint: string, body: TReq, opts?: { duplicateAgency?: string }): Promise<TRes> {
  const res = await fetch(url(endpoint), {
    method: "POST",
    // THE LOCALIZED SERVER ERRORS (Nicolae's ruling, 2026-10-27): the ACTIVE locale rides
    // every request as Accept-Language — read at call time, so a language switch on the page
    // immediately changes what the server answers in (the localized error texts, PRD §5.10).
    headers: { "Content-Type": "application/json", Accept: "application/json", "Accept-Language": getLocale() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 409)
      // mockup wording (candidate-apply.html): the duplicate check names the agency and
      // points at the header "Already registered? Check my status" link (PRD §5.5 friendly reject).
      throw new Error(
        t("err.duplicate", { agency: opts?.duplicateAgency ?? config.agencyName })
      );
    const text = await res.text().catch(() => "");
    throw new Error(
      `${t("err.http", { status: String(res.status) })}${text ? `: ${text.slice(0, 200)}` : ""}.`
    );
  }
  return normalizeKeys((await res.json()) as unknown) as TRes;
}

// ---------------------------------------------------------------------------
// Wire models — mirror the server DTO/STO/envelope shapes (HRNS.Models, HRNS.Web.CQRS).
// ---------------------------------------------------------------------------

export interface WorkCategoryDTO { Id: string; Name: string }
export interface JobTypeDTO { Id: string; WorkCategoryId: string; Name: string }
export interface RegionDTO { Id: string; CountryCode: string; Name: string }

/** DocumentTypes registry reference (document screening questions carry the required type). */
export interface DocumentTypeRefLike {
  Id?: string;
  Name?: string | null;
}

/** ProviderSites record reference (server: HRNS.Models.SystemParams.ProviderSiteRef). */
export interface ProviderSiteRefLike {
  Id?: string;
  HostName?: string | null;
  Description?: string | null;
  BannerLink?: string | null;
}

/** Countries registry reference (server: HRNS.Models.Registries.Countries.CountryRef). */
export interface CountryRefLike {
  Id?: string;
  Code?: string | null;
  Name?: string | null;
}

/** The deployment's public chatbot config (GetSiteChatbotConfigAsync, chatbot PRD §5.2/§7;
 * 2026-10-03 ruling). PUBLIC payload: only the bot @handle + enabled flag — the bot token and
 * the webhook secret never leave the server. Deployment-global (no agency id — one bot per
 * deployment today; per-agency bots later will scope this by agency). */
export interface SiteChatbotConfigDTO {
  TelegramBotUsername?: string | null;
  TelegramEnabled?: boolean;
  /** THE INBOUND NUMBER (chatbot PRD §15/§16): the WABA sender digits THE CANDIDATES message
   *  (wa.me/{inbound}?text={handshakeToken}) — public by design; empty/missing = an older server
   *  build, the deep link then falls back to the deployment env / the candidate's number. */
  WhatsAppInboundNumber?: string | null;
  WhatsAppEnabled?: boolean;
}

/** The deployment's public chatbot config, or null when unreachable/not yet deployed.
 * The caller falls back to the build-time env value (older server builds). The site fetches
 * it ONCE at startup — the bot handle is not agency-bound (see the payload doc above). */
export async function fetchSiteChatbotConfig(): Promise<SiteChatbotConfigDTO | null> {
  if (!isLiveApi) return null; // demo mode: no live server — the env fallback decides
  try {
    const res = await post<Record<string, never>, LoadDTOWebResponse<SiteChatbotConfigDTO>>(
      ENDPOINTS.siteChatbotConfig,
      {} as never,
    );
    return (res.Items ?? [])[0] ?? null;
  } catch (e) {
    // Server down / 502 / an older build without the endpoint: the Telegram choice then
    // follows the VITE env fallback — same behavior as before this endpoint existed.
    console.warn("Site chatbot config unavailable — falling back to the deployment env.", e);
    return null;
  }
}

/** The anonymous public-agencies payload (GetPublicStaffingAgenciesAsync, PRD §5.1 team-sync 6). */
export interface StaffingAgencyDTO {
  Id: string;
  Name: string;
  /** Public sales copy — rendered on the landing (falls back to the deployment config). */
  PublicDescription?: string | null;
  /** Contract internals — nulled by the anonymous endpoint (public payload privacy). */
  TaxId?: string | null;
  Address?: string | null;
  /** The wizard's country step scopes by it (2026-10-02 ruling). */
  ActivityCountry?: CountryRefLike | null;
  /** The big-platform sign-in site the "Check my status" links resolve to (PRD §4.1). */
  InternalProviderSite?: ProviderSiteRefLike | null;
  /** The presentation site itself (this deployment) — never the status/login link target. */
  PublicProviderSite?: ProviderSiteRefLike | null;
}

/** Builds an https URL from a ProviderSites reference HostName. */
export function siteUrl(site?: ProviderSiteRefLike | null): string {
  const host = (site?.HostName ?? "").trim();
  if (!host) return "";
  return /^https?:\/\//i.test(host) ? host : `https://${host}`;
}

/**
 * The candidate status/login target (PRD §5.1, §13 row 43 — 2026-09-30 ruling, confirmed
 * 2026-10-02): the selected agency's **InternalProviderSite** (big-platform sign-in site);
 * the deployment-configured VITE_AGENCY_PORTAL_URL is only the fallback when nothing is
 * selected/known. PublicProviderSite is this presentation site itself — never the target.
 */
export function agencyPortalUrl(agency?: StaffingAgencyDTO | null): string {
  return siteUrl(agency?.InternalProviderSite) || config.agencyPortalUrl;
}

/** StaffingScreeningAnswerType (server): 0 = yes/no, 1 = document upload, 2 = free text (PRD §4.11),
 *  3 = one answer of a list the agency defined (PRD §21.6). */
export type ScreeningAnswerType = 0 | 1 | 2 | 3;

/** One possible answer of a "List of answers" question, its text in the requested language. */
export interface StaffingScreeningQuestionOptionRef {
  Id: string;
  DisplayOrder: number;
  Text: string;
}

export interface StaffingScreeningQuestionDTO {
  Id: string;
  StaffingAgencyId: string;
  CountryCode: string;
  DisplayOrder: number;
  QuestionText: string;
  /** How the question is answered: yes/no · document upload · free text (PRD §4.11, 2026-10-01). */
  AnswerType?: ScreeningAnswerType;
  /** Document question: the system document type the applicant has to provide. */
  DocumentType?: DocumentTypeRefLike | null;
  /** "Shown to" scope: no category = all applicants, otherwise one or more work categories (2026-10-21),
   *  each narrowed or not to some of its job types (2026-10-20; none of a category = the whole category). */
  WorkCategories?: { Id?: string }[] | null;
  JobTypes?: { Id?: string; WorkCategoryId?: string }[] | null;
  /** Manual questions are manager-added per candidate — the wizard never shows them (IsManual=false is requested). */
  IsManual?: boolean;
  /** Kept for the registration wizard (2026-09-30): true for document questions. */
  RequiresDocumentUpload?: boolean;
  /** "List of answers" question: its answers in display order (the candidate picks one). */
  Options?: StaffingScreeningQuestionOptionRef[];
}

/** Per-agency requested sign-up document (GetStaffingAgencyDocumentTypesAsync, PRD §4.12). */
export interface RegistrationDocumentTypeDTO {
  Id: string;
  StaffingAgencyId: string;
  DocumentName: string;
  DisplayOrder: number;
}

export interface StaffingScreeningAnswerSTO {
  QuestionId: string;
  AnswerText: string;
  /** "List of answers" question: the picked option (the server checks it belongs to the question). */
  SelectedOptionId?: string | null;
}

/** Mirrors SaveSTOWebRequest<T> (HRNS.Web.CQRS) for anonymous saves. */
interface SaveSTOWebRequest<T> {
  Items: T[];
  /** Trusted deployment configuration — server fallback route to the page-owning agency (§4.10). */
  CurrentCompanyAccount?: { Id?: string };
}

/** Mirrors LoadDTOWebRequest/{LoadDTOWebResponse} — the query filter rides in `PropsFilter`. */
interface LoadDTOWebRequest<TFilter> {
  PropsFilter?: Partial<TFilter>;
}
interface LoadDTOWebResponse<T> {
  Items?: T[];
}

/** Mirrors StaffingCandidateSTO (HRNS.Models.Staffing.StaffingCandidates). */
export interface StaffingCandidateSTO {
  /** Created by the UI (crypto.randomUUID) and passed in — the server does NOT modify STOs (2026-09-30 ruling). */
  Id: string;
  /** The ONE agency selected on the landing page; the server validates it against the calling site host (PRD §4.10, §13 row 43). */
  StaffingAgencyId?: string;
  FirstName: string;
  LastName: string;
  BirthDate?: string;
  /** PRD §24.7 — server StaffingCandidateGender: Male = 0, Female = 1, PreferNotToDisclose = 2 (mandatory). */
  Gender: number;
  TaxId: string;
  CountryOfResidence: string;
  EmailAddress?: string;
  WhatsAppNumber?: string;
  ProfileStatus: number; // StaffingCandidateProfileStatus.Pending
  SourceType: number; // StaffingCandidateSourceType.SelfRegistered
  WantedJobs: { Id: string }[];
  ActivityRegions: { Id: string }[];
  ScreeningAnswers: StaffingScreeningAnswerSTO[];
  /** Chatbot PRD §3: the candidate's PRIMARY chatbot channel — WhatsApp=1 (default), Telegram=2. */
  PrimaryChatbotChannel?: PrimaryChatbotChannel;
}

/** The save response carries no data — the page already knows the candidate Id it generated and passed in the STO. */
interface SaveResponse {
  IsSucceeded: boolean;
  Error?: string;
}

export interface ContactVerificationItem {
  /** UI-GENERATED item id (platform save convention: every STO item carries one — the
   * CommandBase validation rejects items without an id with "Missing entry ID"). */
  Id?: string;
  CandidateId: string;
  /** StaffingCandidateCandidateContactChannel: Email=0, WhatsApp=1, Telegram=2 (chatbot PRD §5.2). */
  Channel: 0 | 1 | 2;
  Address: string;
  Code?: string;
}

// ---------------------------------------------------------------------------
// Demo-mode data — used only when VITE_API_BASE_URL is not configured.
// Mirrors the mellow-controls mockup (candidate-apply.html).
// ---------------------------------------------------------------------------

const DEMO = {
  categories: [
    { Id: "cat-hosp", Name: "Events & Hospitality" },
    { Id: "cat-wh", Name: "Warehouse" },
  ],
  jobTypes: [
    { Id: "jt-bartender", WorkCategoryId: "cat-hosp", Name: "Bartender" },
    { Id: "jt-server", WorkCategoryId: "cat-hosp", Name: "Server" },
    { Id: "jt-chef", WorkCategoryId: "cat-hosp", Name: "Chef" },
    { Id: "jt-kitchen", WorkCategoryId: "cat-hosp", Name: "Kitchen specialist" },
    { Id: "jt-forklift", WorkCategoryId: "cat-wh", Name: "Forklift driver" },
    { Id: "jt-admin", WorkCategoryId: "cat-wh", Name: "Administrative" },
    { Id: "jt-clerk", WorkCategoryId: "cat-wh", Name: "Clerk" },
  ],
  regions: [
    { Id: "rg-mdn", CountryCode: "ES", Name: "Madrid north" },
    { Id: "rg-mdc", CountryCode: "ES", Name: "Madrid center" },
    { Id: "rg-mds", CountryCode: "ES", Name: "Madrid south" },
    { Id: "rg-bcn", CountryCode: "ES", Name: "Barcelona" },
    { Id: "rg-vlc", CountryCode: "ES", Name: "Valencia" },
    { Id: "rg-agp", CountryCode: "ES", Name: "Malaga" },
    { Id: "rg-pan", CountryCode: "FR", Name: "Paris north" },
    { Id: "rg-pas", CountryCode: "FR", Name: "Paris south" },
  ],
  questions: [
    { Id: "q-events", StaffingAgencyId: "", CountryCode: "", DisplayOrder: 1, QuestionText: "Have you worked events or hospitality shifts before?", AnswerType: 0, RequiresDocumentUpload: false },
    { Id: "q-english", StaffingAgencyId: "", CountryCode: "", DisplayOrder: 2, QuestionText: "Which languages do you speak?", AnswerType: 2 } as StaffingScreeningQuestionDTO,
    { Id: "q-food", StaffingAgencyId: "", CountryCode: "", DisplayOrder: 3, QuestionText: "Do you hold a valid food handler certificate?", AnswerType: 0, WorkCategories: [{ Id: "cat-hosp" }], RequiresDocumentUpload: false },
    { Id: "q-laborrisk", StaffingAgencyId: "", CountryCode: "", DisplayOrder: 4, QuestionText: "Please upload your labor risk prevention certificate", AnswerType: 1, DocumentType: { Id: "dt-labor", Name: "Labor risk prevention certificate" }, RequiresDocumentUpload: true },
  ] as StaffingScreeningQuestionDTO[],
  documentTypes: [
    { Id: "dt-labor", StaffingAgencyId: "", DocumentName: "Labor risk prevention certificate", DisplayOrder: 1 },
  ],
};

// ---------------------------------------------------------------------------

/** Wizard step "upload document" (PRD 5.1/4.12, 2026-09-30): sends each attached file to the
 * anonymous `api/StaffingCandidate/SaveRegistrationDocumentFileAsync` — multipart/form-data;
 * ALL identifiers are UI-generated UUIDs (document, file), per the platform save convention.
 * The server stores the document (status Pending), forwards the content to the file server
 * (provider-host storage location) and creates the PendingReview candidate-document row.
 * Used by the doc-type screening questions AND the agency's requested sign-up documents; a
 * screening question's file carries the question id, which links the document to its question. */
export async function uploadRegistrationDocument(
  candidateId: string,
  staffingAgencyId: string,
  documentName: string,
  file: File,
  questionId?: string,
): Promise<void> {
  if (!isLiveApi) return; // demo mode: nothing is sent

  const form = new FormData();
  form.append("documentId", crypto.randomUUID());
  form.append("fileId", crypto.randomUUID());
  form.append("candidateId", candidateId);
  form.append("staffingAgencyId", staffingAgencyId);
  form.append("documentName", documentName);
  if (questionId) form.append("questionId", questionId);
  form.append("file", file);

  const res = await fetch(url(ENDPOINTS.registrationDocument), {
    method: "POST",
    headers: { "Accept-Language": getLocale() },
    body: form,
  });
  if (!res.ok) throw new Error(t("err.uploadFailed", { name: documentName, status: String(res.status) }));
  const data = (await res
    .json()
    .then(normalizeKeys)
    .catch(() => null)) as SaveResponse | null;
  if (data && data.IsSucceeded === false) {
    throw new Error(data.Error || t("err.uploadFailedGeneric"));
  }
}

/**
 * Public agencies lookup (PRD 5.1, 2026-09-30): the server returns, by the calling site's
 * host (PublicProviderSite), the agencies on this deployment. The landing page renders them
 * as single-select cards (the candidate applies to exactly ONE — 2026-10-02, §13 row 43);
 * the selected Id is passed with the registration STO.
 */
export async function fetchAgencies(): Promise<StaffingAgencyDTO[]> {
  if (!isLiveApi) {
    // demo mode only — a placeholder agency so the UI stays previewable offline
    return [{
      Id: "demo-agency",
      Name: t("demo.agencyName"),
      PublicDescription: t("landing.genericBlurb"),
      PublicProviderSite: { Id: "demo-public", HostName: config.agencyDomain, BannerLink: null },
      InternalProviderSite: { Id: "demo-internal", HostName: siteHostOf(config.agencyPortalUrl), BannerLink: null },
    }];
  }
  try {
    const res = await post<Record<string, never>, LoadDTOWebResponse<StaffingAgencyDTO>>(ENDPOINTS.publicAgencies, {} as never);
    return res.Items ?? [];
  } catch (e) {
    // Server down / 502 / any failure: NO fabricated agency — the page shows "No agency
    // available" (top bar + selection bar) and hides the status links (PRD §5.1, 2026-10-02).
    console.warn("Public agencies lookup failed — no agencies can be advertised.", e);
    return [];
  }
}

export interface AgencyWizardDataDTO {
  StaffingAgencyId: string;
  Name: string;
  /** The agency's sales copy (contract PublicDescription; null → the page falls back). */
  PublicDescription?: string | null;
  /** The agency's activity country (contract) — the wizard's single-country step source. */
  ActivityCountry?: CountryRefLike | null;
  /** The STANDARD regions of the activity country (system registry, not agency-editable). */
  Regions?: RegionDTO[];
  /** The work categories holding the agency's proposed job types. */
  WorkCategories?: WorkCategoryDTO[];
  /** The job types of the agency's contract (its proposed jobs / Jobs & Regions selection). */
  JobTypes?: JobTypeDTO[];
}

/** The wizard data of the SELECTED agency — one read, taken from the agency's contract. */
export interface LoadedAgencyWizard {
  agencyId: string;
  name: string;
  description: string | null;
  activityCountry: { code: string; label: string } | null;
  categories: WorkCategoryDTO[];
  jobTypes: JobTypeDTO[];
  regions: RegionDTO[];
}

/**
 * Loads the SELECTED AGENCY'S wizard data — `api/StaffingAgency/GetStaffingAgencyWizardData`
 * (anonymous; the agency id is required): the agency's contract-driven dictionaries — its
 * activity country + the standard regions of that country + the work categories/job types
 * of its proposed jobs (PRD §5.1/§16.1, 2026-10-02 ruling). Re-fetched on every agency
 * selection change; live failures THROW (error + retry on the page — no demo fabrications).
 */
/**
 * The language of the dictionary names (PRD 18.4, 14 #45): the site's active locale (en | es | fr) rides every
 * dictionary read as `LanguageCode`, so the server answers with the category / job type / region names in that
 * language (base names when a row has no translation or the code is unknown). Language switches re-fetch (App.tsx).
 */
export const languageFilter = (): { LanguageCode: string } => ({ LanguageCode: getLocale() });

export async function fetchAgencyWizardData(agencyId: string): Promise<LoadedAgencyWizard> {
  if (!isLiveApi) {
    // demo mode: the bundled data stands in for the agency's contract — an activity
    // country (Spain) + its regions only (as the real contract-driven endpoint returns)
    return {
      agencyId,
      name: t("demo.agencyName"),
      description: t("landing.genericBlurb"),
      activityCountry: { code: "ES", label: "Spain" },
      categories: DEMO.categories,
      jobTypes: DEMO.jobTypes,
      regions: DEMO.regions.filter((r) => r.CountryCode === "ES"),
    };
  }

  const res = await post<LoadDTOWebRequest<{ StaffingAgencyId?: string; LanguageCode?: string }>, LoadDTOWebResponse<AgencyWizardDataDTO>>(
    ENDPOINTS.agencyWizardData,
    { PropsFilter: { StaffingAgencyId: agencyId || undefined, ...languageFilter() } },
  );
  const wizard = (res.Items ?? [])[0];
  if (!wizard) throw new Error(t("toast.dictLoadFailed"));

  const country = wizard.ActivityCountry;
  return {
    agencyId,
    name: wizard.Name ?? "",
    description: wizard.PublicDescription ?? null,
    activityCountry: country?.Code
      ? { code: country.Code, label: country.Name || country.Code.toUpperCase() }
      : null,
    categories: wizard.WorkCategories ?? [],
    jobTypes: wizard.JobTypes ?? [],
    regions: wizard.Regions ?? [],
  };
}

/** Screening questions per (SELECTED agency, country) — default (non-manual) questions only:
 * manual ones are manager-added per candidate and never asked at registration (PRD §4.11). */
export async function fetchScreeningQuestions(agencyId: string, countryCode: string): Promise<StaffingScreeningQuestionDTO[]> {
  if (!isLiveApi) return DEMO.questions;
  try {
    const res = await post<LoadDTOWebRequest<{ StaffingAgencyId?: string; CountryCode?: string; IsManual?: boolean; LanguageCode?: string }>, LoadDTOWebResponse<StaffingScreeningQuestionDTO>>(
      ENDPOINTS.screeningQuestions,
      { PropsFilter: { StaffingAgencyId: agencyId || undefined, CountryCode: countryCode, IsManual: false, ...languageFilter() } },
    );
    return (res.Items ?? [])
      .filter((q) => !q.IsManual)
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder);
  } catch (e) {
    console.warn("Screening questions unavailable — continuing without them.", e);
    return [];
  }
}

/** The agency's requested sign-up documents (PRD §4.12) — optional, non-blocking uploads. */
export async function fetchRegistrationDocumentTypes(agencyId: string): Promise<RegistrationDocumentTypeDTO[]> {
  if (!isLiveApi) return DEMO.documentTypes;
  try {
    const res = await post<LoadDTOWebRequest<{ StaffingAgencyId?: string }>, LoadDTOWebResponse<RegistrationDocumentTypeDTO>>(
      ENDPOINTS.agencyDocumentTypes,
      { PropsFilter: { StaffingAgencyId: agencyId || undefined } },
    );
    return (res.Items ?? []).sort((a, b) => a.DisplayOrder - b.DisplayOrder);
  } catch (e) {
    console.warn("Agency document types unavailable — continuing without them.", e);
    return [];
  }
}

export interface RegisterResult {
  candidateId: string;
}

export async function registerCandidate(
  sto: StaffingCandidateSTO,
  opts?: { duplicateAgency?: string },
): Promise<RegisterResult> {
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 600));
    return { candidateId: sto.Id };
  }
  const body: SaveSTOWebRequest<StaffingCandidateSTO> = {
    Items: [sto],
  };
  if (config.pageAgencyCompanyAccountId) {
    body.CurrentCompanyAccount = { Id: config.pageAgencyCompanyAccountId };
  }
  const res = await post<SaveSTOWebRequest<StaffingCandidateSTO>, SaveResponse>(ENDPOINTS.register, body, opts);
  if (!res.IsSucceeded) {
    throw new Error(res.Error || t("err.registerFailed"));
  }
  // The page created and passed the candidate Id in the STO — the server does not modify
  // STOs (2026-09-30 ruling), and SaveSTOWebResponse carries no data back.
  return { candidateId: sto.Id };
}

export type ContactChannel = 0 | 1; // Email = 0, WhatsApp = 1 (PRD §7)

/** Requests a verification code — the OPTIONAL e-mail verification only: the WhatsApp confirmation
 *  is the candidate-initiated wa.me handshake (requestWhatsAppLink; the OTP path is RETIRED —
 *  chatbot PRD §15/§16, T-SA-18.16/18.19; no codes are ever sent for WhatsApp). */

export async function requestContactCode(candidateId: string, channel: ContactChannel, address: string): Promise<void> {
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 400));
    toastDemoCode();
    return;
  }
  // every platform save item carries a UI-GENERATED Id (CommandBase validation:
  // "Missing entry ID") — 2026-10-02 fix of the live verification request
  const res = await post<SaveSTOWebRequest<ContactVerificationItem>, SaveResponse>(ENDPOINTS.requestVerification, {
    Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: channel, Address: address }],
  });
  // a refused request answers HTTP 200 + IsSucceeded=false (the platform save envelope)
  if (!res.IsSucceeded) throw new Error(res.Error || t("email.err.send"));
}

/** The password-setup mail outcome (server StaffingPasswordMailStatus, T-SA-18.14). */
export type PasswordMailStatus = "Sent" | "NoInternalSite" | "SendFailed";

interface ContactVerificationResponse extends SaveResponse {
  /** The outcome, or a resend refusal code (NotEligible / PasswordAlreadySet / TooManyRequests). */
  PasswordMailStatus?: PasswordMailStatus | "NotEligible" | "PasswordAlreadySet" | "TooManyRequests" | null;
}

/** Confirms the code. An e-mail confirmation also returns whether the password-setup link was
 *  sent (null for WhatsApp, where no mail is involved). */
export async function confirmContactCode(
  candidateId: string,
  channel: ContactChannel,
  address: string,
  code: string,
): Promise<PasswordMailStatus | null> {
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 400));
    if (code !== lastDemoEmailCode)
      throw new Error(t("err.codeIncorrect.email"));
    return "Sent";
  }
  const res = await post<SaveSTOWebRequest<ContactVerificationItem>, ContactVerificationResponse>(
    ENDPOINTS.confirmVerification,
    { Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: channel, Address: address, Code: code }] },
  );
  // a wrong/expired code answers HTTP 200 + IsSucceeded=false — it must NOT read as confirmed
  if (!res.IsSucceeded)
    throw new Error(res.Error || t("email.err.confirm"));
  return (res.PasswordMailStatus as PasswordMailStatus | null | undefined) ?? null;
}

/** Re-sends the password-setup link to the candidate's confirmed e-mail (T-SA-18.14). */
export async function resendPasswordLink(candidateId: string, email: string): Promise<PasswordMailStatus | null> {
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 400));
    return "Sent";
  }
  const res = await post<SaveSTOWebRequest<ContactVerificationItem>, ContactVerificationResponse>(
    ENDPOINTS.resendPasswordLink,
    { Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: 0, Address: email }] },
  );
  if (!res.IsSucceeded) {
    // the refusals come as codes - translated here (the server text is English-only);
    // the typed class lets the Log-in button still navigate on an ANSWERED refusal
    if (res.PasswordMailStatus === "NotEligible")
      throw new PasswordLinkRefusedError("NotEligible", t("email.password.err.notEligible"));
    if (res.PasswordMailStatus === "PasswordAlreadySet")
      throw new PasswordLinkRefusedError("PasswordAlreadySet", t("email.password.err.passwordSet"));
    if (res.PasswordMailStatus === "TooManyRequests")
      throw new PasswordLinkRefusedError("TooManyRequests", t("email.password.err.tooMany"));
    throw new PasswordLinkRefusedError(res.PasswordMailStatus ?? "", res.Error || t("email.password.err.resend"));
  }
  return (res.PasswordMailStatus as PasswordMailStatus | null | undefined) ?? null;
}

/** A resend answered IsSucceeded=false: carries the server refusal code (or "" for a plain
 *  error). "PasswordAlreadySet" is NOT a mail failure - the account is usable, the caller
 *  (the Log-in button) still switches to the verify/confirm section. */
export class PasswordLinkRefusedError extends Error {
  /** The server refusal code ("NotEligible" / "PasswordAlreadySet"), "" when none. */
  status: string;
  constructor(status: string, message: string) {
    super(message);
    this.name = "PasswordLinkRefusedError";
    this.status = status;
  }
}

/** The candidate's PRIMARY chatbot channel at self-registration (chatbot PRD §3):
 *  WhatsApp = 1, Telegram = 2 on the server enum; "none" only for legacy rows. */
export type PrimaryChatbotChannel = 0 | 1 | 2;

/**
 * Requests a Telegram chat-binding ROW and returns the binding token (chatbot PRD §5.2).
 * The wizard GENERATES the token client-side (2026-09-30 "UI creates the ids" convention -
 * the platform save returns no data): it builds t.me/{bot}?start={token} and later polls
 * the binding status with it. Server-side validation requires 16+ characters.
 */
export async function requestTelegramBinding(candidateId: string): Promise<string> {
  const token = crypto.randomUUID();
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 300));
    return token;
  }
  await post<SaveSTOWebRequest<ContactVerificationItem>, unknown>(ENDPOINTS.requestTelegramBinding, {
    Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: 2, Address: "telegram", Code: token }],
  });
  return token;
}

export interface TelegramBindingStatus {
  /** pending | awaiting_phone | bound | expired | unknown | unbound — 'awaiting_phone' means
   *  the /start binding is consumed and the PHONE NUMBER is not confirmed yet (the
   *  two-step Telegram confirmation, 2026-10-10): the page shows the
   *  [Confirm phone number by Telegram] button. 'Bound' = the FULL confirmation. */
  Status: string;
  Bound: boolean;
  /** Candidate-level channel flags — set by the TOKEN-LESS probe (Token empty):
   *  "is this candidate already verified on this channel?" — the re-entered verify
   *  step shows "already verified" instead of re-issuing tokens. EmailValidated gates
   *  the status links (the confirm-email step instead of the redirect, 2026-10-07). */
  WhatsAppValidated?: boolean;
  TelegramValidated?: boolean;
  EmailValidated?: boolean;
}

/** Polls the binding status; the (candidate, token) pair is the proof. */
/** THE CANDIDATE-LEVEL CHATBOT STATUS (chatbot PRD §15/16, T-SA-18.19): the PROPERLY-NAMED probe
 *  the WhatsApp verify cards poll — ALWAYS answers for a valid candidate id (no row semantics):
 *  the three validated flags only. The candidate id rides PropsFilter.Ids (the generic shape). */
export async function getChatbotBindingStatus(candidateId: string): Promise<TelegramBindingStatus | null> {
  if (!isLiveApi) {
    // demo: no live webhook — the candidate-level flags never flip; the demo flip happens in the card
    return { Status: "unbound", Bound: false };
  }
  const res = await post<LoadDTOWebRequest<{ Ids: string[] }>, LoadDTOWebResponse<TelegramBindingStatus>>(
    ENDPOINTS.chatbotBindingStatus,
    { PropsFilter: { Ids: [candidateId] } }
  );
  const item = (res.Items ?? [])[0] as unknown as Record<string, unknown> | undefined;
  if (!item) return null;
  const r1 = (k: string): unknown => item[k] ?? item[k.charAt(0).toLowerCase() + k.slice(1)];
  return {
    Status: String(r1("Status") ?? "unbound"),
    Bound: r1("Bound") === true,
    WhatsAppValidated: r1("WhatsAppValidated") === true,
    TelegramValidated: r1("TelegramValidated") === true,
    EmailValidated: r1("EmailValidated") === true,
  };
}

export async function getTelegramBindingStatus(candidateId: string, token: string): Promise<TelegramBindingStatus | null> {
  if (!isLiveApi) {
    // local demo: binding "completes" after one poll with any token — the CANDIDATE-LEVEL
    // flags stay untouched here (the WhatsApp handshake demo flip happens in its own poll)
    return { Status: "bound", Bound: true };
  }
  // Token EMPTY = the candidate-level probe ("already verified?" — no row status is leaked).
  const res = await post<LoadDTOWebRequest<{ CandidateId: string; Token: string }>, LoadDTOWebResponse<TelegramBindingStatus>>(
    ENDPOINTS.getTelegramBindingStatus,
    { PropsFilter: { CandidateId: candidateId, Token: token ?? "" } }
  );
  const item = res.Items?.[0] as unknown as Record<string, unknown> | undefined;
  if (!item) return null;
  // CASE-TOLERANT MAPPING (chatbot PRD §15): both key conventions reached this field
  // historically (the platform serializes DTO props Pascal-case; some envelopes camel);
  // the WhatsApp handshake poll must flip on WhatsAppValidated whichever the payload uses.
  const r1 = (k: string): unknown => item[k] ?? item[k.charAt(0).toLowerCase() + k.slice(1)];
  return {
    Status: String(r1("Status") ?? "unknown"),
    Bound: r1("Bound") === true,
    WhatsAppValidated: r1("WhatsAppValidated") === true,
    TelegramValidated: r1("TelegramValidated") === true,
    EmailValidated: r1("EmailValidated") === true,
  };
}

/**
 * THE CANDIDATE-INITIATED WHATSAPP CONFIRMATION (chatbot PRD §15 / Amendment 1.3): issues the
 * wa.me handshake row — the CLIENT generates the unguessable token (the platform convention);
 * the page builds `https://wa.me/{number}?text={token}` from it and polls the candidate-level
 * status (getTelegramBindingStatus with an empty token — it answers WhatsAppValidated). No
 * OTP code is requested or typed: the CANDIDATE initiates by sending the prefilled message
 * and the WhatsApp webhook completes the confirmation. Supersedes the 2026-09-29 code entry.
 */
export async function requestWhatsAppLink(candidateId: string, address: string): Promise<string> {
  const token = crypto.randomUUID();
  if (!isLiveApi) {
    await new Promise((r) => setTimeout(r, 400));
    return token; // demo: the poll flips after one iteration (see getBindingStatus demo)
  }
  try {
    const res = await post<SaveSTOWebRequest<ContactVerificationItem>, SaveResponse>(ENDPOINTS.requestWhatsAppLink, {
      Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: 1, Address: address, Code: token }],
    });
    if (!res.IsSucceeded) throw new Error(res.Error || t("verify.err.link"));
    return token;
  } catch (e) {
    // SURFACE THE REAL REASON (the production finding, 2026-10-19): a 404 names an OLD API build
    // (the endpoint missing); the response envelope's error surfaces first when present
    const axiosErr = e as {
      response?: { status?: number; data?: { error?: string } };
      isAxiosError?: boolean;
    };
    const serverError = axiosErr.response?.data?.error;
    if (serverError) throw new Error(serverError);
    if (axiosErr.response?.status === 404) {
      throw new Error(
        "The WhatsApp link endpoint is not available on this API build (404) - redeploy the server with the current staffing-agency-module."
      );
    }
    if (axiosErr.response?.status !== undefined) {
      throw new Error(`The WhatsApp link request failed (HTTP ${axiosErr.response.status}).`);
    }
    throw e;
  }
}

/**
 * TWO-STEP TELEGRAM CONFIRMATION (2026-10-10), step 2's re-send: the [Confirm phone number
 * by Telegram] button — the (candidate, token) pair authorizes (as the poll does); the server
 * re-delivers the phone-share consent message (the request_contact keyboard) into the linked
 * chat. Nothing about the chat id / phone ever crosses to the page.
 */
export async function resendTelegramPhoneRequest(candidateId: string, token: string | null): Promise<void> {
  if (!isLiveApi) return;
  if (!token) throw new Error("The Telegram link has expired — refresh the page to link again.");
  await post<SaveSTOWebRequest<ContactVerificationItem>, unknown>(ENDPOINTS.resendTelegramPhoneRequest, {
    Items: [{ Id: crypto.randomUUID(), CandidateId: candidateId, Channel: 2, Address: "telegram", Code: token }],
  });
}
/** The demo code toast — EMAIL only: WhatsApp has no demo code anymore (the confirmation is the
 *  candidate-initiated wa.me handshake; no codes are ever sent; §15/16, T-SA-18.16/18.19). */
let lastDemoEmailCode = "";
function toastDemoCode() {
  const code = String(Math.floor(100000 + Math.random() * 900000));
  lastDemoEmailCode = code;
  window.setTimeout(() => {
    toast(t("demo.emailCode", { code }));
  }, 650);
}

/** Document name for a doc-type question: the system document type when provided (the contract
 * management side records documents per type), else the question text (2026-09-30 behaviour). */
export function screeningDocumentName(q?: StaffingScreeningQuestionDTO | null): string {
  return (q?.DocumentType?.Name ?? "").trim() || q?.QuestionText || t("questions.docFallback");
}

/** Countries for the wizard fallback: derived from the (server-provided) regions. */
export function countriesFromRegions(regions: { CountryCode: string }[]): { code: string; label: string }[] {
  const codes = [...new Set(regions.map((r) => r.CountryCode).filter(Boolean))].sort();
  return codes.map((c) => ({ code: c, label: c.length === 2 ? c.toUpperCase() : c }));
}

/** Host part of a configured URL (demo-mode helper for the internal-site reference). */
function siteHostOf(value: string): string {
  try {
    return new URL(value).host;
  } catch {
    return value.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  }
}

function toast(message: string) {
  const t = document.getElementById("toast");
  if (!t) return;
  t.textContent = message;
  t.classList.add("on");
  clearTimeout((toast as never as { h?: number }).h);
  (toast as never as { h?: number }).h = window.setTimeout(() => t.classList.remove("on"), 6000);
}